import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Flex, Portal } from "@chakra-ui/react";
import { ChatIcon } from "@chakra-ui/icons";
import ChatBubble from "./ChatBubble";
import RecordingBubble from "./RecordingBubble";
import ReportsBubble from "./ReportsBubble";

/**
 * Bubble Container Widget
 *
 * A wrapper that contains both ChatBubble and RecordingBubble
 * positioned using flex row for side-by-side placement.
 *
 * Both bubbles share the same Portal and positioning, so they
 * appear as a unified widget at the bottom-right of the screen.
 */
const BubbleContainer = ({ currentPatientId, currentPatientData }) => {
  const getViewportLimits = useCallback(() => {
    const maxWidth = Math.max(320, window.innerWidth - 20);
    const maxHeight = Math.max(420, window.innerHeight - 20);
    return { maxWidth, maxHeight };
  }, []);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position] = useState({ right: 20, bottom: 20 });
  const initialLimits = getViewportLimits();
  const [chatPosition, setChatPosition] = useState(() => ({
    left: Math.max(0, window.innerWidth - Math.min(400, initialLimits.maxWidth) - 90),
    top: Math.max(0, window.innerHeight - Math.min(650, initialLimits.maxHeight) - 20),
  }));
  const [chatSize, setChatSize] = useState({
    width: Math.min(400, initialLimits.maxWidth),
    height: Math.min(650, initialLimits.maxHeight),
  });
  const [isResizing, setIsResizing] = useState(false);
  const [activePatientId, setActivePatientId] = useState(currentPatientId || null);
  const [activePatientData, setActivePatientData] = useState(
    currentPatientData || null,
  );
  const [activeDocumentContent, setActiveDocumentContent] = useState(null);
  const prevPatientContextPatientIdRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const resizeStartRef = useRef({
    x: 0,
    y: 0,
    width: 400,
    height: 650,
  });
  const resizeFrameRef = useRef(null);
  const pendingResizeRef = useRef(null);
  const chatRef = useRef(null);

  useEffect(() => {
    if (currentPatientId) {
      setActivePatientId(currentPatientId);
      setActivePatientData(currentPatientData || null);
    }
  }, [currentPatientData, currentPatientId]);

  useEffect(() => {
    const handlePatientContextUpdate = (e) => {
      const { patientId, patientData, documentContent } = e.detail || {};
      if (!patientId) return;
      if (prevPatientContextPatientIdRef.current !== patientId) {
        prevPatientContextPatientIdRef.current = patientId;
        setActiveDocumentContent(null);
      }
      setActivePatientId(patientId);
      setActivePatientData(patientData || null);
      if (documentContent !== undefined) {
        setActiveDocumentContent(documentContent ?? null);
      }
    };
    window.addEventListener("patientContextUpdate", handlePatientContextUpdate);
    return () =>
      window.removeEventListener(
        "patientContextUpdate",
        handlePatientContextUpdate,
      );
  }, []);

  const clampChatPosition = useCallback(
    (left, top, width = chatSize.width, height = chatSize.height) => ({
      left: Math.max(0, Math.min(window.innerWidth - width, left)),
      top: Math.max(0, Math.min(window.innerHeight - height, top)),
    }),
    [chatSize.height, chatSize.width],
  );

  const handleChatHeaderMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = chatRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    setIsDragging(true);
  }, []);

  const handleResizeMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      resizeStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        width: chatSize.width,
        height: chatSize.height,
      };
      setIsResizing(true);
    },
    [chatSize.height, chatSize.width],
  );

  // Handle drag
  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e) => {
      if (isResizing) {
        const { maxWidth, maxHeight } = getViewportLimits();
        const deltaX = e.clientX - resizeStartRef.current.x;
        const deltaY = e.clientY - resizeStartRef.current.y;
        const nextWidth = Math.max(
          320,
          Math.min(maxWidth, resizeStartRef.current.width + deltaX),
        );
        const nextHeight = Math.max(
          420,
          Math.min(maxHeight, resizeStartRef.current.height + deltaY),
        );
        pendingResizeRef.current = { width: nextWidth, height: nextHeight };
        if (!resizeFrameRef.current) {
          resizeFrameRef.current = window.requestAnimationFrame(() => {
            const pending = pendingResizeRef.current;
            if (!pending) {
              resizeFrameRef.current = null;
              return;
            }
            setChatSize(pending);
            setChatPosition((prev) =>
              clampChatPosition(prev.left, prev.top, pending.width, pending.height),
            );
            pendingResizeRef.current = null;
            resizeFrameRef.current = null;
          });
        }
        return;
      }
      if (!isDragging || !chatRef.current) return;

      // Calculate new position based on mouse position minus the initial offset
      const newLeft = e.clientX - dragOffsetRef.current.x;
      const newTop = e.clientY - dragOffsetRef.current.y;

      setChatPosition(clampChatPosition(newLeft, newTop));
    };

    const handleMouseUp = () => {
      if (pendingResizeRef.current) {
        const pending = pendingResizeRef.current;
        setChatSize(pending);
        setChatPosition((prev) =>
          clampChatPosition(prev.left, prev.top, pending.width, pending.height),
        );
        pendingResizeRef.current = null;
      }
      if (resizeFrameRef.current) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
      setIsDragging(false);
      setIsResizing(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      if (resizeFrameRef.current) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [clampChatPosition, getViewportLimits, isDragging, isResizing]);

  useEffect(() => {
    const handleWindowResize = () => {
      const { maxWidth, maxHeight } = getViewportLimits();
      setChatSize((prev) => ({
        width: Math.min(prev.width, maxWidth),
        height: Math.min(prev.height, maxHeight),
      }));
      setChatPosition((prev) =>
        clampChatPosition(
          prev.left,
          prev.top,
          Math.min(chatSize.width, maxWidth),
          Math.min(chatSize.height, maxHeight),
        ),
      );
    };

    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [chatSize.height, chatSize.width, clampChatPosition, getViewportLimits]);

  // Toggle chat expanded state
  const handleToggle = useCallback((expanded) => {
    setIsExpanded(expanded);
  }, []);

  const handleCloseChat = useCallback(() => {
    setIsExpanded(false);
  }, []);

  return (
    <Portal>
      <Box
        position="fixed"
        right={`${position.right}px`}
        bottom={`${position.bottom}px`}
        zIndex={9998}
        className="bubble-bg"
      >
<Flex
          direction="row"
          align="flex-end"
          gap="10px"
        >
          {/* Reports Bubble - leftmost, opens View Reports for last patient row */}
          <ReportsBubble />

          {/* Recording Bubble - positioned in the middle (always visible) */}
          <RecordingBubble
            currentPatientId={activePatientId}
            currentPatientData={activePatientData}
            isExpanded={isExpanded}
            onToggle={handleToggle}
          />
          {isExpanded ? (
            <Box
              width="60px"
              height="60px"
              borderRadius="full"
              bg="blue.600"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 4px 20px rgba(0, 0, 0, 0.15)"
              _hover={{ bg: "blue.500" }}
              transition="background-color 0.2s"
              cursor="pointer"
              onClick={handleCloseChat}
              aria-label="Close AI Assistant"
            >
              <ChatIcon color="white" boxSize={6} />
            </Box>
          ) : (
            <ChatBubble
              currentPatientId={activePatientId}
              currentPatientData={activePatientData}
              documentContent={activeDocumentContent}
              isExpanded={isExpanded}
              onToggle={handleToggle}
              onClose={handleCloseChat}
              hideCollapsedBubble={false}
            />
          )}
        </Flex>
      </Box>

      {isExpanded && (
        <Box
          ref={chatRef}
          position="fixed"
          left={`${chatPosition.left}px`}
          top={`${chatPosition.top}px`}
          zIndex={9999}
          width={`${chatSize.width}px`}
          height={`${chatSize.height}px`}
          cursor={isDragging ? "grabbing" : "default"}
          transition={isResizing ? "none" : "box-shadow 0.15s ease"}
          willChange={isResizing ? "width, height" : "auto"}
        >
          <Box width="100%" height="100%" position="relative">
            <ChatBubble
              currentPatientId={activePatientId}
              currentPatientData={activePatientData}
              documentContent={activeDocumentContent}
              isExpanded={isExpanded}
              onToggle={handleToggle}
              onClose={handleCloseChat}
              bubbleWidth={chatSize.width}
              bubbleHeight={chatSize.height}
              onHeaderMouseDown={handleChatHeaderMouseDown}
              hideCollapsedBubble={true}
              isResizing={isResizing}
            />
            <Box
              position="absolute"
              right="0"
              bottom="0"
              width="18px"
              height="18px"
              cursor="nwse-resize"
              zIndex={2}
              onMouseDown={handleResizeMouseDown}
              _before={{
                content: '""',
                position: "absolute",
                right: "3px",
                bottom: "3px",
                width: "10px",
                height: "10px",
                borderRight: "2px solid #A0AEC0",
                borderBottom: "2px solid #A0AEC0",
              }}
            />
          </Box>
        </Box>
      )}
    </Portal>
  );
};

export default BubbleContainer;
